import { createContext, ReactNode, useState } from 'react';

export interface UserContextType {
  jwt: string;
  setJwt(jwt: string): void;
  isAdmin: boolean;
  setIsAdmin(isAdmin: boolean): void;
  isSuperAdmin: boolean;
  setIsSuperAdmin(isSuperAdmin: boolean): void;
  name: string;
  setName(name: string): void;
  email: string;
  setEmail(email: string): void;
}

/**
 * Context exposing the authenticated user's JWT plus `isAdmin`/`isSuperAdmin`
 * flags and the `name`/`email` resolved via `/users/me` (separated, not merged
 * like in HSM Admin v1). The guard resolves all of this once and writes it
 * here; consumers like `UserPill` read it instead of re-fetching.
 */
export const UserContext = createContext<UserContextType>({
  jwt: '',
  setJwt: () => undefined,
  isAdmin: false,
  setIsAdmin: () => undefined,
  isSuperAdmin: false,
  setIsSuperAdmin: () => undefined,
  name: '',
  setName: () => undefined,
  email: '',
  setEmail: () => undefined,
});

/**
 * Provider that owns the auth state via `useState` and exposes it through
 * `UserContext`. Mount it once near the root, above `AuthenticationGuard`
 * (the guard writes here after parsing the JWT cookie).
 */
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [jwt, setJwt] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  return (
    <UserContext.Provider
      value={{
        jwt,
        setJwt,
        isAdmin,
        setIsAdmin,
        isSuperAdmin,
        setIsSuperAdmin,
        name,
        setName,
        email,
        setEmail,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
